import { useMemo } from "react";
import { trpc } from "@/lib/trpc";
import { isRelevantType } from "@/lib/location-filters";
import type { MapFilterState } from "@/components/map/MapFilters";
import type { BoundingBox } from "@/types/domain";

export interface MarkerDatum {
  id: string;
  name: string;
  primary_type: string | null;
  ownership: string;
  address: string | null;
  lat: number;
  lng: number;
  position: google.maps.LatLngLiteral;
  hasSportField: boolean | null;
  confidence: number | null;
  isMuted: boolean;
  userRatingCount: number;
  isReviewed: boolean;
  isRejected: boolean;
  source: string | null;
  approvedByCity: boolean;
  approvedBySponsor: boolean;
  sportTypes: string[];
}

export function useMapData(
  filters: MapFilterState,
  viewportBounds: BoundingBox | null
) {
  const { data: scansData } = trpc.scans.list.useQuery({});

  const activeScan = useMemo(() => {
    if (!scansData?.scans) return null;
    return (
      scansData.scans.find((s) =>
        ["scanning", "screenshotting", "classifying", "website_classifying"].includes(s.status)
      ) ?? null
    );
  }, [scansData]);

  // Only send bounds for "uncollected" mode — collections are already bounded sets
  const hasUncollected = filters.collectionIds.includes("__uncollected__");
  const realIds = filters.collectionIds.filter((id) => id !== "__uncollected__");
  const needsBounds = hasUncollected;

  const { data: mapData, isLoading } = trpc.locations.getMapData.useQuery(
    {
      collectionIds: realIds.length > 0 ? realIds : undefined,
      includeUncollected: hasUncollected || undefined,
      bounds: needsBounds && viewportBounds ? viewportBounds : undefined,
    },
    {
      enabled: filters.collectionIds.length > 0 && (!needsBounds || !!viewportBounds),
      refetchInterval: activeScan ? 30_000 : false,
    }
  );

  const markerData = useMemo<MarkerDatum[]>(() => {
    if (!mapData) return [];
    return mapData
      .filter((loc) => {
        if (filters.classification !== "all") {
          if (filters.classification === "sport_field" && loc.has_sport_field !== true) return false;
          if (filters.classification === "no_field" && loc.has_sport_field !== false) return false;
          if (filters.classification === "unclassified" && loc.has_sport_field !== null) return false;
        }

        if (filters.ownership !== "all" && loc.ownership !== filters.ownership) {
          return false;
        }

        if (filters.locationTypes.length > 0) {
          if (!loc.primary_type || !filters.locationTypes.includes(loc.primary_type)) {
            return false;
          }
        }

        if (filters.reviewStatus !== "all") {
          if (filters.reviewStatus === "reviewed" && !loc.is_reviewed) return false;
          if (filters.reviewStatus === "rejected" && !loc.is_rejected) return false;
          if (filters.reviewStatus === "unreviewed" && (loc.is_reviewed || loc.is_rejected)) return false;
        }

        if (filters.source !== "all") {
          if (loc.source !== filters.source) return false;
        }

        if (filters.sportTypes.length > 0) {
          const hasMatch = filters.sportTypes.some((st) => loc.sport_types.includes(st));
          if (!hasMatch) return false;
        }

        return true;
      })
      .map((loc) => {
        const isImported = loc.source === "city" || loc.source === "external_import";
        const anySportField = loc.has_sport_field === true;
        const isExcludedOwnership = loc.ownership === "private" || loc.ownership === "institutional";
        const confirmedSportField = anySportField && !isExcludedOwnership;
        const muted = loc.is_rejected || (!isImported && !confirmedSportField && !isRelevantType(loc.primary_type));
        return {
          id: loc.id,
          name: loc.name,
          primary_type: loc.primary_type,
          ownership: loc.ownership,
          address: loc.address,
          lat: loc.lat,
          lng: loc.lng,
          position: { lat: loc.lat, lng: loc.lng },
          hasSportField: loc.has_sport_field,
          confidence: loc.confidence,
          isMuted: muted,
          userRatingCount: loc.user_rating_count,
          isReviewed: loc.is_reviewed,
          isRejected: loc.is_rejected,
          source: loc.source,
          approvedByCity: loc.approved_by_city,
          approvedBySponsor: loc.approved_by_sponsor,
          sportTypes: loc.sport_types,
        };
      });
  }, [mapData, filters.classification, filters.ownership, filters.locationTypes, filters.reviewStatus, filters.source, filters.sportTypes]);

  // Viewport culling is now largely handled server-side via bounds param.
  // Keep a lightweight client-side cull for the buffer edge (server sends 10% extra).
  const culledMarkerData = useMemo(() => {
    if (!viewportBounds) return markerData;
    return markerData.filter(
      (loc) =>
        loc.lat >= viewportBounds.south &&
        loc.lat <= viewportBounds.north &&
        loc.lng >= viewportBounds.west &&
        loc.lng <= viewportBounds.east
    );
  }, [markerData, viewportBounds]);

  return {
    markerData,
    culledMarkerData,
    mapData,
    isLoading,
  };
}
